import { Typography } from '@euler/app/components/typography';
import { AnimationSource, SoundFile } from '@euler/assets';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Audio } from 'expo-av';
import { LinearGradient } from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  LayoutRectangle,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const HoorayScreen = wrapNavigatorScreen(
  ({
    type,
    soundType,
    buttonText,
    shareButtonText = '分享报告',
    onPress,
    onShare,
  }: {
    type: keyof typeof AnimationSource;
    soundType?: keyof typeof SoundFile;
    buttonText: string;
    shareButtonText?: string;
    onPress?: (navigation: StackNavigationProp<any>) => any;
    onShare?: (navigation: StackNavigationProp<any>) => any;
  }) => {
    const { rpx } = useResponsiveContext();

    const navigation = useNavigation<StackNavigationProp<any>>();
    const soundRef = useRef<Audio.Sound>();

    const [layout, setLayout] = useState<LayoutRectangle>();

    const playSound = useCallback(async (source: any) => {
      try {
        const { sound } = await Audio.Sound.createAsync(source);
        soundRef.current = sound;
        await sound.playAsync();
      } catch (e) {
        console.error('error load sound file: ', e);
      }
    }, []);

    const handlePress = useCallback(() => {
      onPress?.(navigation);
    }, [navigation, onPress]);

    const handleShare = useCallback(() => {
      onShare?.(navigation);
    }, [navigation, onShare]);

    useEffect(() => {
      if (soundType != null) {
        void playSound(SoundFile[soundType]);
      }
      return () => {
        void soundRef.current?.unloadAsync();
      };
    }, [playSound, soundType]);

    let lottieViewSize: { width: number; height: number } | undefined =
      undefined;

    if (layout != null) {
      lottieViewSize = {
        height: layout.height / 2,
        width: (layout.height / 2) * (516 / 463),
      };
      if (lottieViewSize.width > layout.width - rpx(64)) {
        lottieViewSize.width = layout.width - rpx(64);
        lottieViewSize.height = (463 / 516) * lottieViewSize.width;
      }
    }

    return (
      <LinearGradient
        colors={['#009FD4', '#2669db']}
        css={`
          flex: 1;
          justify-content: center;
          align-items: center;
        `}
        onLayout={e => {
          setLayout(e.nativeEvent.layout);
        }}
      >
        <View>
          {lottieViewSize != null && (
            <LottieView
              source={AnimationSource[type]}
              autoPlay
              loop={false}
              css={`
                width: ${lottieViewSize.width}px;
                height: ${lottieViewSize.height}px;
              `}
            />
          )}
        </View>
        {lottieViewSize != null && (
          <>
            <AnimatedButton
              title={buttonText}
              delay={500}
              onPress={handlePress}
              css={`
                margin-top: ${rpx(32)}px;
                width: ${lottieViewSize.width * 0.6}px;
              `}
            />
            {onShare != null && (
              <AnimatedButton
                delay={600}
                title={shareButtonText}
                onPress={handleShare}
                css={`
                  margin-top: ${rpx(16)}px;
                  width: ${lottieViewSize.width * 0.6}px;
                `}
              />
            )}
          </>
        )}
      </LinearGradient>
    );
  },
  { headerShown: false, gestureEnabled: false },
);

const AnimatedButton = memo(
  ({
    style,
    title,
    delay,
    onPress,
  }: {
    style?: StyleProp<ViewStyle>;
    title: string;
    delay: number;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <MotiView
        from={{ opacity: 0, translateY: rpx(150) }}
        animate={{ opacity: 1, translateY: 0 }}
        transition={{ type: 'timing', duration: 350 }}
        delay={delay}
        style={style}
      >
        <TouchableOpacity
          css={`
            background-color: #fff;
            box-shadow: 0 0 ${rpx(5)}px rgba(0, 0, 0, 0.1);
            elevation: 1;
            padding: 0 ${rpx(32)}px;
            height: ${rpx(56)}px;
            border-radius: ${rpx(28)}px;
            align-items: center;
            justify-content: center;
          `}
          onPress={onPress}
        >
          <Typography.Label light size={rpx(20)}>
            {title}
          </Typography.Label>
        </TouchableOpacity>
      </MotiView>
    );
  },
);
